// Mail section layout. Two modes:
//   workspace: /clients/:id/mail/*  — scoped to one client
//   global:    /mail/*              — super admin aggregate view
//
// Renders top context bar (workspace mode only) + persistent left rail + Outlet.

import { useEffect, useState, useCallback } from 'react';
import { NavLink, Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useRole } from '../hooks/useRole';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function NavItem({ to, label, count }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        'mail-nav-item' + (isActive ? ' mail-nav-item--active' : '')
      }
    >
      <span className="mail-nav-label">{label}</span>
      {count != null && count > 0 ? <span className="mail-nav-count">{count}</span> : null}
    </NavLink>
  );
}

function MailBreadcrumbs({ clientId, isGlobalMode }) {
  const { getToken } = useAuth();
  const { isSuperAdmin, isTeamMember } = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [client, setClient] = useState(null);
  const [mailbox, setMailbox] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clientQuery, setClientQuery] = useState('');

  // Fetch client name for breadcrumb
  useEffect(() => {
    if (!clientId || isGlobalMode) { setClient(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && !cancelled) setClient(await r.json());
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [clientId, isGlobalMode, getToken]);

  // Fetch mailbox / campaign label if we're on a detail page
  useEffect(() => {
    const { mailboxId, campaignId } = params;
    if (mailboxId) {
      (async () => {
        try {
          const token = await getToken();
          const r = await fetch(`${API_BASE}/mailboxes/${mailboxId}`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.ok) setMailbox(await r.json());
        } catch (_) {}
      })();
    } else { setMailbox(null); }
    if (campaignId) {
      (async () => {
        try {
          const token = await getToken();
          const r = await fetch(`${API_BASE}/campaigns/${campaignId}`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.ok) setCampaign(await r.json());
        } catch (_) {}
      })();
    } else { setCampaign(null); }
  }, [params, getToken]);

  const loadAllClients = useCallback(async () => {
    if (allClients.length > 0) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/clients`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setAllClients(await r.json());
    } catch (_) {}
  }, [getToken, allClients.length]);

  // Parse URL to determine section + leaf
  // /mail/{section}                        → Mail / {section}
  // /mail/{section}/{id}                   → Mail / {section} / {leaf}
  // /clients/:id/mail/{section}            → Mail / {client} / {section}
  // /clients/:id/mail/{section}/{id}       → Mail / {client} / {section} / {leaf}
  const pathParts = location.pathname.split('/').filter(Boolean);
  const mailIdx = pathParts.indexOf('mail');
  const section = pathParts[mailIdx + 1] || null;
  const hasLeaf = !!pathParts[mailIdx + 2];

  const sectionLabels = {
    inbox: 'Inbox', outbox: 'Outbox', sent: 'Sent',
    campaigns: 'Campaigns', mailboxes: 'Mailboxes', pools: 'Pools', personas: 'Personas',
    warmup: 'Warmup', metrics: 'Metrics',
    suppressions: 'Suppressions', snippets: 'Snippets', signatures: 'Signatures',
  };
  const sectionLabel = section ? (sectionLabels[section] || section) : null;
  const sectionPath = isGlobalMode ? `/mail/${section}` : `/clients/${clientId}/mail/${section}`;

  let leafLabel = null;
  if (hasLeaf) {
    if (params.mailboxId && mailbox) leafLabel = mailbox.email_address;
    else if (params.campaignId && campaign) leafLabel = campaign.name;
    else leafLabel = pathParts[mailIdx + 2];
  }

  const segStyle = { color: '#666', textDecoration: 'none', cursor: 'pointer' };
  const sepStyle = { color: '#CCC', margin: '0 8px' };
  const activeStyle = { color: '#111', fontWeight: 600 };

  return (
    <div style={{
      background: '#FBF9F7',
      borderBottom: '1px solid #EAE6E1',
      padding: '8px 36px',
      display: 'flex',
      alignItems: 'center',
      fontSize: 13,
      position: 'relative',
      minHeight: 36,
    }}>
      {/* Mail root */}
      {(isSuperAdmin || isTeamMember) ? (
        <span style={hasLeaf || section || !isGlobalMode ? segStyle : activeStyle}
              onClick={() => navigate('/mail/inbox')}>
          Mail
        </span>
      ) : (
        <span style={activeStyle}>Mail</span>
      )}

      {/* Client (workspace mode only) */}
      {!isGlobalMode && client && (
        <>
          <span style={sepStyle}>/</span>
          {(isSuperAdmin || isTeamMember) ? (
            <span
              style={{ ...((section || hasLeaf) ? segStyle : activeStyle), display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => { setPickerOpen(!pickerOpen); loadAllClients(); }}
            >
              {client.name}
              <span style={{ fontSize: 9, color: '#999' }}>▾</span>
            </span>
          ) : (
            <span style={(section || hasLeaf) ? { color: '#666' } : activeStyle}>
              {client.name}
            </span>
          )}
        </>
      )}

      {/* Section */}
      {section && (
        <>
          <span style={sepStyle}>/</span>
          {hasLeaf ? (
            <span style={segStyle} onClick={() => navigate(sectionPath)}>{sectionLabel}</span>
          ) : (
            <span style={activeStyle}>{sectionLabel}</span>
          )}
        </>
      )}

      {/* Leaf */}
      {hasLeaf && leafLabel && (
        <>
          <span style={sepStyle}>/</span>
          <span style={activeStyle}>{leafLabel}</span>
        </>
      )}

      {/* Client switcher dropdown */}
      {pickerOpen && (isSuperAdmin || isTeamMember) && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 60,
            background: 'white',
            border: '1px solid #EAE6E1',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            minWidth: 260,
            maxHeight: 340,
            overflowY: 'auto',
            zIndex: 50,
            padding: 4,
          }}
          onMouseLeave={() => { setPickerOpen(false); setClientQuery(''); }}
        >
          <input
            type="text"
            placeholder="Search clients…"
            value={clientQuery}
            onChange={(e) => setClientQuery(e.target.value)}
            autoFocus
            style={{
              width: '100%', padding: '8px 10px', border: '1px solid #EAE6E1',
              borderRadius: 5, fontSize: 13, marginBottom: 4, boxSizing: 'border-box', outline: 'none',
            }}
          />
          <button
            onClick={() => { setPickerOpen(false); navigate('/mail/inbox'); }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
              background: 'none', color: '#8B1A1A', border: 'none', borderRadius: 5,
              fontSize: 13, cursor: 'pointer', fontWeight: 500,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAFA'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            ← All clients (global view)
          </button>
          <div style={{ height: 1, background: '#EAE6E1', margin: '4px 0' }} />
          {allClients.length === 0 && <div style={{ padding: 12, color: '#888', fontSize: 13 }}>Loading…</div>}
          {allClients
            .filter((c) => {
              if (!clientQuery) return true;
              const q = clientQuery.toLowerCase();
              return (c.name || '').toLowerCase().includes(q) || (c.sector || '').toLowerCase().includes(q);
            })
            .map((c) => (
              <button
                key={c.id}
                onClick={() => { setPickerOpen(false); navigate(`/clients/${c.id}/mail/inbox`); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                  background: c.id === parseInt(clientId) ? '#F1ECEC' : 'none',
                  color: c.id === parseInt(clientId) ? '#8B1A1A' : '#333',
                  border: 'none', borderRadius: 5, fontSize: 13, cursor: 'pointer',
                  fontWeight: c.id === parseInt(clientId) ? 600 : 400,
                }}
                onMouseEnter={(e) => { if (c.id !== parseInt(clientId)) e.currentTarget.style.background = '#FAFAFA'; }}
                onMouseLeave={(e) => { if (c.id !== parseInt(clientId)) e.currentTarget.style.background = 'transparent'; }}
              >
                {c.name}
                {c.sector && <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>· {c.sector}</span>}
              </button>
            ))}
          {clientQuery && allClients.filter((c) => {
            const q = clientQuery.toLowerCase();
            return (c.name || '').toLowerCase().includes(q) || (c.sector || '').toLowerCase().includes(q);
          }).length === 0 && allClients.length > 0 && (
            <div style={{ padding: 12, color: '#888', fontSize: 13 }}>No matches.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MailLayout() {
  const { getToken } = useAuth();
  const { clientId: roleClientId, isSuperAdmin } = useRole();
  const { id: urlClientId } = useParams();
  const location = useLocation();

  // Effective clientId: URL takes precedence; else fall back to user's role-bound clientId
  const effectiveClientId = urlClientId || roleClientId;
  const isWorkspaceMode = !!urlClientId;
  const isGlobalMode = !urlClientId;

  const [counts, setCounts] = useState({ inbox: 0, outbox: 0, campaigns: 0, mailboxes: 0 });

  const buildPath = useCallback((suffix) => {
    return isWorkspaceMode ? `/clients/${urlClientId}/mail/${suffix}` : `/mail/${suffix}`;
  }, [isWorkspaceMode, urlClientId]);

  useEffect(() => {
    let cancelled = false;
    async function loadCounts() {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        let overviewUrl, mailboxesUrl;
        if (isGlobalMode) {
          if (!isSuperAdmin) { setCounts({ inbox: 0, outbox: 0, campaigns: 0, mailboxes: 0 }); return; }
          overviewUrl = `${API_BASE}/admin/metrics/overview`;
          mailboxesUrl = `${API_BASE}/admin/mailboxes`;
        } else {
          if (!effectiveClientId) return;
          overviewUrl = `${API_BASE}/clients/${effectiveClientId}/metrics/overview`;
          mailboxesUrl = `${API_BASE}/clients/${effectiveClientId}/mailboxes`;
        }
        const [overview, mailboxes] = await Promise.all([
          fetch(overviewUrl, { headers }).then(r => r.json()).catch(() => ({})),
          fetch(mailboxesUrl, { headers }).then(r => r.json()).catch(() => []),
        ]);
        if (cancelled) return;
        setCounts({
          inbox: 0,
          outbox: overview.queued || 0,
          campaigns: (overview.active_campaigns || 0) + (overview.paused_campaigns || 0),
          mailboxes: Array.isArray(mailboxes) ? mailboxes.length : 0,
        });
      } catch (_) {}
    }
    loadCounts();
    return () => { cancelled = true; };
  }, [effectiveClientId, isGlobalMode, isSuperAdmin, getToken, location.pathname]);

  return (
    <div>
      <MailBreadcrumbs clientId={effectiveClientId} isGlobalMode={isGlobalMode} />
      <div className="mail-layout">
        <aside className="mail-rail">
          <div className="mail-rail-group">
            <div className="mail-rail-heading">Mail</div>
            <NavItem to={buildPath('inbox')} label="Inbox" count={counts.inbox} />
            <NavItem to={buildPath('outbox')} label="Outbox" count={counts.outbox} />
            <NavItem to={buildPath('sent')} label="Sent" />
          </div>
          <div className="mail-rail-group">
            <div className="mail-rail-heading">Outbound</div>
            <NavItem to={buildPath('campaigns')} label="Campaigns" count={counts.campaigns} />
            <NavItem to={buildPath('mailboxes')} label="Mailboxes" count={counts.mailboxes} />
            <NavItem to={buildPath('pools')} label="Pools" />
            {isWorkspaceMode && <NavItem to={buildPath('personas')} label="Personas" />}
            <NavItem to={buildPath('warmup')} label="Warmup" />
            <NavItem to={buildPath('metrics')} label="Metrics" />
          </div>
          <div className="mail-rail-group">
            <div className="mail-rail-heading">Settings</div>
            <NavItem to={buildPath('suppressions')} label="Suppressions" />
            <NavItem to={buildPath('snippets')} label="Snippets" />
            <NavItem to={buildPath('signatures')} label="Signatures" />
          </div>
        </aside>
        <main className="mail-main">
          <Outlet context={{ clientId: effectiveClientId, isGlobalMode, isWorkspaceMode }} />
        </main>
      </div>
    </div>
  );
}
